'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { HoloCard } from './HoloCard';
import { StatsRow } from './StatsRow';
import { InfoCard } from './InfoCard';
import { StatusPill } from './StatusPill';
import { ScanHistoryModal } from './ScanHistoryModal';
import { DocumentsCard } from './DocumentsCard';
import { GoogleReviewCard } from './GoogleReviewCard';
import { PRODUCT_FALLBACK, type VaultEntry } from '@/lib/vault';

interface AlreadyRegisteredStateProps {
  code: string;
  entry: VaultEntry;
  scanCount: number;
  uniqueDevices: number;
}

export function AlreadyRegisteredState({ code, entry, scanCount, uniqueDevices }: AlreadyRegisteredStateProps) {
  const t = useTranslations('public');
  const [scanHistoryOpen, setScanHistoryOpen] = useState(false);
  const [infoExpanded, setInfoExpanded] = useState(false);

  const serial = entry.serialNumber != null ? String(entry.serialNumber).padStart(7, '0') : null;
  const productName = entry.productName || PRODUCT_FALLBACK;

  const productionDateFormatted = entry.productionDate
    ? new Date(entry.productionDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const useByDateFormatted = entry.useByDate
    ? new Date(entry.useByDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div className="flex flex-col gap-3 px-4" style={{ paddingBottom: 100 }}>
      {/* Holographic certificate — amber variant */}
      <HoloCard serial={entry.serialNumber} flipIn variant="amber" title={t('alreadyRegistered.title')} productName={productName} />

      {/* Stats — scan count is tappable to open history */}
      <div className="pub-reveal pub-reveal-2">
        <StatsRow
          items={[
            { value: scanCount, label: t('verified.totalScans'), color: 'var(--pub-amber, #f59e0b)', onPress: () => setScanHistoryOpen(true) },
            { value: uniqueDevices, label: t('alreadyRegistered.devices') },
            { value: t('alreadyRegistered.notYours'), label: t('verified.ownership'), color: 'var(--pub-amber, #f59e0b)' },
          ]}
        />
      </div>

      {/* Info banner — this code was registered by another device */}
      <div className="pub-reveal pub-reveal-2">
        <button
          onClick={() => setInfoExpanded(!infoExpanded)}
          style={{
            width: '100%',
            background: 'var(--pub-surface-2)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            borderRadius: 14,
            padding: '12px 16px',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center flex-shrink-0"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="rgb(245, 158, 11)" strokeWidth="2" width="16" height="16">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--pub-text-primary)' }}>
                {t('alreadyRegistered.infoTitle')}
              </div>
              <div style={{ fontSize: 11, color: 'var(--pub-text-muted)', marginTop: 2 }}>
                {t('alreadyRegistered.infoSub')}
              </div>
            </div>
            <svg
              viewBox="0 0 24 24" fill="none" stroke="var(--pub-text-muted)" strokeWidth="2" width="14" height="14"
              style={{ flexShrink: 0, transition: 'transform 0.2s', transform: infoExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>

          {infoExpanded && (
            <div
              style={{
                marginTop: 12,
                paddingTop: 12,
                borderTop: '1px solid var(--pub-border)',
                fontSize: 12,
                lineHeight: 1.6,
                color: 'var(--pub-text-secondary)',
              }}
            >
              {t('alreadyRegistered.infoBody')}
            </div>
          )}
        </button>
      </div>

      {/* Leave a Google review — kept above the fold, in the compliance card's spot */}
      <div className="pub-reveal pub-reveal-3">
        <GoogleReviewCard />
      </div>

      {/* Manufacturing & Traceability data */}
      <div className="pub-reveal pub-reveal-3">
        <InfoCard
          rows={[
            ...(serial ? [{ label: t('compliance.serialNumber'), value: serial, mono: true }] : []),
            ...(entry.batchName ? [{ label: t('verified.batch'), value: entry.batchName, mono: true }] : []),
            ...(productionDateFormatted ? [{ label: t('compliance.manufactured'), value: productionDateFormatted }] : []),
            ...(useByDateFormatted ? [{ label: t('compliance.useBy'), value: useByDateFormatted }] : []),
            ...(entry.manufacturingLot ? [{ label: t('compliance.lot'), value: entry.manufacturingLot, mono: true }] : []),
            ...(entry.productionLine ? [{ label: t('compliance.productionLine'), value: entry.productionLine, mono: true }] : []),
            ...(entry.qualityCheck !== undefined ? [{
              label: t('compliance.qualityCheck'),
              value: <StatusPill variant={entry.qualityCheck ? 'green' : 'red'}>
                {entry.qualityCheck ? t('compliance.passed') : t('compliance.failed')}
              </StatusPill>,
            }] : []),
            ...(entry.operatorName ? [{
              label: t('compliance.operator'),
              value: `${entry.operatorName}${entry.operatorDepartment ? ` · ${entry.operatorDepartment}` : ''}`,
            }] : []),
            ...(entry.clientName ? [{ label: t('compliance.brand'), value: entry.clientName }] : []),
            {
              label: t('verified.status'),
              value: <StatusPill variant="green">&#9679; {t('verified.genuine')}</StatusPill>,
            },
            {
              label: t('verified.ownership'),
              value: <StatusPill variant="amber">{t('alreadyRegistered.otherDevice')}</StatusPill>,
            },
          ]}
        />
      </div>

      {/* Document Access */}
      <div className="pub-reveal pub-reveal-4">
        <DocumentsCard />
      </div>

      {/* Contact link */}
      <div className="pub-reveal pub-reveal-4">
        <a
          href={`/contact?reason=report-suspicious&code=${encodeURIComponent(code)}`}
          className="pub-btn pub-btn-secondary"
          style={{ textAlign: 'center', textDecoration: 'none', display: 'block' }}
        >
          {t('alreadyRegistered.contactUs')}
        </a>
      </div>

      {/* Rescan note */}
      <div className="pub-reveal pub-reveal-4">
        <div
          style={{
            textAlign: 'center',
            fontSize: 11,
            color: 'var(--pub-text-muted)',
            lineHeight: 1.5,
            padding: '4px 16px',
          }}
        >
          {t('alreadyRegistered.rescanNote')}
        </div>
      </div>

      {/* Scan history modal */}
      <ScanHistoryModal
        open={scanHistoryOpen}
        onClose={() => setScanHistoryOpen(false)}
        code={code}
      />
    </div>
  );
}
